import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import api from "../../utils/api";
import TimePickerWheel from "./TimePickerWheel";

/**
 * NEW BOOKING SYSTEM - ADD OPENING COMPONENT
 * 
 * This component allows business owners to create new booking slots
 * for their services. It provides a form to select business, service,
 * date, and time. Duration is automatically determined by the selected service.
 */

const AddOpening = ({ companyList, auth: { user }, onOpeningCreated }) => {
  const [formData, setFormData] = useState({
    companyId: "",
    serviceId: "",
    date: moment().format("YYYY-MM-DD"),
    startTime: "09:00"
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdSlotInfo, setCreatedSlotInfo] = useState(null);

  // Set default company and service when companyList loads
  useEffect(() => {
    if (companyList && companyList.length > 0 && !formData.companyId) {
      const firstCompany = companyList[0];
      setFormData(prev => ({
        ...prev,
        companyId: firstCompany._id
      }));
      setSelectedCompany(firstCompany);
    }
  }, [companyList, formData.companyId]);

  // Set default service when company changes
  useEffect(() => {
    if (selectedCompany && selectedCompany.services && selectedCompany.services.length > 0) {
      const firstService = selectedCompany.services[0];
      setFormData(prev => ({
        ...prev,
        serviceId: firstService._id
      }));
    }
  }, [selectedCompany]);

  const onChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "companyId") {
      const company = companyList.find(c => c._id === value);
      setSelectedCompany(company);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        serviceId: company?.services?.[0]?._id || ""
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setShowSuccess(false);

    try {
      // Get the selected service to use its duration
      const selectedService = selectedCompany?.services?.find(s => s._id === formData.serviceId);
      if (!selectedService) {
        setMessage("Please select a service");
        setLoading(false);
        return;
      }

      // Calculate start and end times using the service's duration
      const startDateTime = moment(`${formData.date} ${formData.startTime}`).toDate();
      const serviceDuration = selectedService.service_duration || 30; // Default to 30 if not set
      const endDateTime = moment(startDateTime).add(serviceDuration, 'minutes').toDate();
      // Sent as a real local-midnight instant (not a bare "YYYY-MM-DD" string) so the
      // server doesn't mis-parse it as UTC midnight and shift it onto the wrong day.
      const localMidnight = moment(`${formData.date} 00:00`).toDate();

      // Get service name for success message
      const serviceName = selectedService.name || "Service";

      const slotData = {
        companyId: formData.companyId,
        serviceId: formData.serviceId,
        date: localMidnight,
        startTime: startDateTime,
        endTime: endDateTime
      };

      await api.post("/bookingslots", slotData);

      if (typeof onOpeningCreated === "function") {
        onOpeningCreated();
      }

      // Store created slot info for success display
      setCreatedSlotInfo({
        serviceName,
        date: moment(formData.date).format("dddd, MMMM D, YYYY"),
        time: moment(formData.startTime, "HH:mm").format("h:mm A"),
        duration: serviceDuration
      });
      
      setShowSuccess(true);
      setMessage("Opening added successfully!");
      
      // Reset form
      setFormData(prev => ({
        ...prev,
        date: moment().format("YYYY-MM-DD"),
        startTime: "09:00"
      }));

      // Hide success state after 4 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setMessage("");
        setCreatedSlotInfo(null);
      }, 4000);

    } catch (error) {
      console.error("Error creating slot:", error);
      setMessage("Error creating opening. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Don't render if user has no businesses
  if (!companyList || companyList.length === 0) {
    return <div>No businesses found</div>
  }

  return (
    <div className="border-8 border-grey">
      <p className="text-center text-2m bg-orange text-white px-3 card-head font-semibold m-0">
        Add Opening
      </p>
      <div className="p-3">
        <form onSubmit={onSubmit}>
          {/* Business Selection */}
          <div className="form-group mb-3">
            <label className="form-label text-sm font-semibold">Business</label>
            <select
              name="companyId"
              value={formData.companyId}
              onChange={onChange}
              className="form-control"
              required
            >
              {companyList.map(company => (
                <option key={company._id} value={company._id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          {/* Service Selection */}
          <div className="form-group mb-3">
            <label className="form-label text-sm font-semibold">Service</label>
            <select
              name="serviceId"
              value={formData.serviceId}
              onChange={onChange}
              className="form-control"
              required
              disabled={!selectedCompany?.services?.length}
            >
              {selectedCompany?.services?.map(service => (
                <option key={service._id} value={service._id}>
                  {service.name} ({service.service_duration} min)
                </option>
              ))}
            </select>
          </div>

          {/* Date Selection */}
          <div className="form-group mb-3">
            <label className="form-label text-sm font-semibold">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={onChange}
              className="form-control"
              min={moment().format("YYYY-MM-DD")}
              required
            />
          </div>

          {/* Start Time */}
          <div className="form-group mb-3">
            <label className="form-label text-sm font-semibold">Start Time</label>
            <TimePickerWheel
              value={formData.startTime}
              onChange={(timeValue) => setFormData(prev => ({ ...prev, startTime: timeValue }))}
            />
          </div>

          {/* Success Display - Prominent confirmation */}
          {showSuccess && createdSlotInfo && (
            <div 
              className="text-center p-4 mb-3 rounded"
              style={{
                backgroundColor: '#d4edda',
                border: '2px solid #28a745',
                animation: 'fadeInScale 0.3s ease-out'
              }}
            >
              <div 
                className="mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#28a745',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  animation: 'checkmark 0.5s ease-out 0.2s both'
                }}
              >
                <svg 
                  width="30" 
                  height="30" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h5 className="text-success mb-2" style={{ fontWeight: 'bold' }}>
                Opening Added!
              </h5>
              <p className="mb-1" style={{ color: '#155724', fontSize: '14px' }}>
                <strong>{createdSlotInfo.serviceName}</strong>
              </p>
              <p className="mb-1" style={{ color: '#155724', fontSize: '13px' }}>
                {createdSlotInfo.date}
              </p>
              <p className="mb-0" style={{ color: '#155724', fontSize: '13px' }}>
                {createdSlotInfo.time} ({createdSlotInfo.duration} min)
              </p>
            </div>
          )}

          {/* Error Message Display */}
          {message && message.includes("Error") && (
            <div className="alert alert-danger mb-3">
              {message}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading || !formData.companyId || !formData.serviceId || showSuccess}
            style={{
              transition: 'all 0.3s ease',
              ...(showSuccess && { backgroundColor: '#28a745', borderColor: '#28a745' })
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : showSuccess ? (
              <>
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ marginRight: '8px', verticalAlign: 'middle' }}
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Added!
              </>
            ) : (
              "Add Opening"
            )}
          </button>

          {/* CSS Animation Keyframes */}
          <style>{`
            @keyframes fadeInScale {
              0% {
                opacity: 0;
                transform: scale(0.9);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes checkmark {
              0% {
                transform: scale(0);
              }
              50% {
                transform: scale(1.2);
              }
              100% {
                transform: scale(1);
              }
            }
          `}</style>
        </form>
      </div>
    </div>
  );
};

AddOpening.propTypes = {
  companyList: PropTypes.array,
  auth: PropTypes.object.isRequired,
  onOpeningCreated: PropTypes.func,
};

const mapStateToProps = state => ({
  companyList: state.company.companylist,
  auth: state.auth
});

export default connect(mapStateToProps)(AddOpening);